import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import FamilyQuestion from "../../components/FamilyQuestion";
import DifficultTopicsQuestion from "../../components/DifficultTopicsQuestion";
import CultureQuestion from "../../components/CultureQuestion";
import { getAuth } from "firebase/auth";
import Stepper from "../../components/Stepper";
import { trackEvent } from "../../utils/Analytics";
import AppetizerLoading from "../../components/AppetizerLoading";

function CustomizeQuestionsView() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const NUM_QUESTIONS = 3;
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Store all user preferences in a single object
  const [userPreferences, setUserPreferences] = useState({
    familyMembers: {},
    familyContext: "",
    difficultTopics: {},
    topicContext: "",
    translationLanguage: "",
    cultureContext: "",
    selectedContext: {},
  });

  useEffect(() => {
    trackEvent(
      "Deck",
      "Customization Step",
      "Landed on Step 1",
      undefined,
      {
        user_id: currentUser?.uid,
        user_email: currentUser?.email,
        step: 1,
        current_url: window.location.href,
      }
    );
    // Only fire once on mount
    // eslint-disable-next-line
  }, []);

  async function generateCards(preferences) {
    console.log("Generating cards with preferences:", preferences);
    try {
      const auth = getAuth();
      const idToken = currentUser ? await auth.currentUser.getIdToken() : null;

      const url = process.env.REACT_APP_GENERATE_PROMPTS_URL_PROD;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
        },
        body: JSON.stringify(preferences),
        mode: "cors"
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Failed to fetch prompts");
      }

      const generatedResponse = await res.json();

      // --- Track deck finished event here ---
      trackEvent(
        "Deck",
        "Finish Deck Creation",
        "User finished deck creation",
        undefined,
        {
          user_id: currentUser?.uid,
          user_email: currentUser?.email,
          deckId: generatedResponse.deckId,
          preferences,
          current_url: window.location.href,
        }
      );
      // --------------------------------------

      // Navigate to the deck view with the generated deckId
      navigate(`/deck/${generatedResponse.deckId}`);

    } catch (err) {
      console.error("API Error:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // Handle navigation between questions
  const handleNextStep = async (response) => {
    // Track step progress
    trackEvent(
      "Deck",
      "Customization Step",
      `Reached Step ${currentStep + 1}`,
      undefined,
      {
        user_id: currentUser?.uid,
        user_email: currentUser?.email,
        step: currentStep + 1,
        current_url: window.location.href,
      }
    );

    if (currentStep === 0) {
      setUserPreferences(prev => ({
        ...prev,
        familyMembers: response.familyMembers,
        familyContext: response.familyContext,
      }));
      setCurrentStep(currentStep + 1);
      return;
    }
    if (currentStep === 1) {
      setUserPreferences(prev => ({
        ...prev,
        translationLanguage: response.translationLanguage,
        cultureContext: response.cultureContext,
        selectedContext: response.selectedContext,
      }));
      setCurrentStep(currentStep + 1);
      return;
    }
    if (currentStep === 2) {
      const updatedPreferences = {
        ...userPreferences,
        difficultTopics: response.difficultTopics,
        topicContext: response.topicContext,
      };
      setUserPreferences(updatedPreferences);

      setIsLoading(true);
      setError(null);

      console.log("Submitting all responses:", updatedPreferences);
      return await generateCards(updatedPreferences);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  if (isLoading) {
    return <AppetizerLoading />;
  }

  if (error) {
    return (
      <div className="pt-24 pb-12 max-w-md mx-auto">
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Pardon us, we're likely experiencing high activity levels and having trouble processing your request! Please try again later, or contact support@imoonshots.com if it happens again in 24 hours. Thank you for your patience.</span>
        </div>
        <button
          onClick={() => generateCards(userPreferences)}
          className="btn btn-primary mt-4"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 flex flex-col items-center min-h-screen bg-[#F9F9F9]">
      <Stepper currentStep={currentStep} />

      <div className="flex items-center justify-center">
        {currentStep === 0 &&
          <FamilyQuestion
            onNextClick={handleNextStep}
            initialValues={{
              familyMembers: userPreferences.familyMembers,
              familyContext: userPreferences.familyContext
            }}
          />
        }
        {currentStep === 1 &&
          <CultureQuestion
            onNextClick={handleNextStep}
            onPreviousClick={handlePreviousStep}
            initialValues={{
              translationLanguage: userPreferences.translationLanguage,
              cultureContext: userPreferences.cultureContext,
              selectedContext: userPreferences.selectedContext
            }}
          />
        }
        {currentStep === 2 &&
          <DifficultTopicsQuestion
            onNextClick={handleNextStep}
            onPreviousClick={handlePreviousStep}
            initialValues={{
              difficultTopics: userPreferences.difficultTopics,
              topicContext: userPreferences.topicContext
            }}
          />
        }
      </div>
    </div>
  );
}

export default CustomizeQuestionsView;
